import React from "react";
import { Link } from "react-router-dom";
import { Cake, CandyCane, Gift, Cookie, CheckCircle2, Sparkles, Wallet } from "lucide-react";
import SakuraDivider from "@/components/SakuraDivider";

const groups = [
  {
    id: "cake-pops",
    title: "Cake Pops",
    subtitle: "Available in classic round, flat pops, and cakesicle styles.",
    icon: Cake,
    accent: "bg-sakura-200 text-sakura-500",
    items: [
      {
        name: "Classic Cake Pops",
        price: "from $36 / dozen",
        desc: "Vanilla or chocolate cake, dipped in your color of choice with simple sprinkles or drizzle details.",
      },
      {
        name: "Custom Cake Pops",
        price: "from $48 / dozen",
        desc: "Styled to match your theme with custom colors, textures, and simple shapes such as hearts, bows, and florals.",
      },
      {
        name: "Detailed / Character Cake Pops",
        price: "$60+ / dozen · custom quote",
        desc: "Highly detailed designs or character-themed pops. Advanced notice required.",
      },
    ],
  },
  {
    id: "dipped-treats",
    title: "Dipped Treats",
    icon: Cookie,
    accent: "bg-sakura-gold/20 text-sakura-gold",
    items: [
      { name: "Chocolate-Covered Pretzels", price: "$18 / dozen" },
      { name: "Dipped Oreos", price: "$20 / dozen" },
      { name: "Rice Krispie Treats", price: "$24 / dozen" },
      { name: "Chocolate-Covered Strawberries", price: "$25 / dozen", desc: "Seasonal availability." },
    ],
    footnote: "Custom colors and drizzle are included. Detailed designs may have additional cost.",
  },
];

// Party Packages — distinct from `groups` because each package has a richer
// shape (serves count, description, includes list) and renders differently.
const partyPackages = [
  {
    id: "petal",
    title: "Petal Package",
    subtitle: "Small Party Package",
    price: "Starting at $95–$110",
    serves: "Feeds approximately 12–18 people",
    includes: [
      "48 treats / 4 dozen",
      "Custom drizzle & decorative details",
    ],
  },
  {
    id: "bloom",
    title: "Bloom Package",
    subtitle: "Medium Party Package",
    price: "Starting at $180–$220",
    serves: "Feeds approximately 25–40 people",
    includes: [
      "96 treats / 8 dozen",
      "Custom colors & themed designs",
    ],
  },
  {
    id: "luxe-garden",
    title: "Luxe Garden Package",
    subtitle: "Large Party Package",
    price: "Starting at $240–$285",
    serves: "Feeds approximately 40–55 people",
    includes: [
      "120 treats / 10 dozen",
      "Luxury decorations & premium detailing",
      "Florals, butterflies, edible glitter, and gold accents available upon request",
    ],
    featured: true,
  },
];

// Policies & Order Information — grouped sections for the bottom of the menu page.
const policies = [
  {
    id: "order",
    title: "Order Policy",
    items: [
      "A 50% non-refundable deposit is required for all orders over $30 to secure your date.",
      "Orders under $30 require full payment upfront.",
      "Custom orders should be placed at least 3–7 days in advance when possible.",
      "Rush orders may be accepted depending on availability and may include an additional fee.",
    ],
  },
  {
    id: "pickup",
    title: "Pickup Policy",
    items: [
      "Please arrive at your scheduled pickup time.",
      "A 15-minute grace period is allowed for pickups.",
      "If you are running late, please communicate as soon as possible.",
      "Orders not picked up within 30 minutes without communication may be canceled and are non-refundable due to the handmade and perishable nature of the treats.",
    ],
  },
  {
    id: "no-show",
    title: "No-Show Policy",
    items: [
      "If a customer does not show up for pickup and does not communicate, the order will be considered abandoned.",
      "No refunds will be given for no-show orders.",
      "Re-delivery or rescheduling may require an additional fee depending on availability.",
    ],
  },
  {
    id: "custom-design",
    title: "Custom Design Policy",
    items: [
      "Every treat box is handmade and custom designed, so slight variations may occur.",
      "While I always do my best to match inspiration photos and themes, exact replicas cannot be guaranteed.",
    ],
  },
  {
    id: "delivery",
    title: "Delivery Policy",
    items: [
      "Delivery is available for an additional fee depending on location.",
      "Please ensure the delivery address provided is correct.",
      "Once an order has been delivered or picked up, Sakura Treats is no longer responsible for damages caused by improper storage, heat exposure, or transportation.",
    ],
  },
  {
    id: "storage",
    title: "Storage Information",
    items: [
      "Chocolate covered treats should be refrigerated until ready to serve.",
      "For best taste and quality, treats are recommended to be enjoyed within 24–48 hours.",
    ],
  },
  {
    id: "refund",
    title: "Refund Policy",
    items: [
      "Due to the custom and perishable nature of our products, refunds are not available once an order has been completed.",
      "If there is an issue with your order, please contact us within 24 hours so we can make it right.",
    ],
  },
];

export default function Menu() {
  return (
    <div data-testid="page-menu">
      <section className="section">
        <div className="mx-auto max-w-5xl text-center">
          <span className="chip"><Sparkles className="h-3 w-3" /> Menu & Pricing</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-sakura-ink">The Sakura Treats Treat List</h1>
          <p className="mt-4 text-sakura-soft max-w-2xl mx-auto">
            Handcrafted cake pops, dipped treats, and custom dessert boxes — made fresh to order. Final pricing depends on design complexity, quantity, and theme details. A custom quote will be provided for all orders.
          </p>
          <SakuraDivider />
        </div>
      </section>

      <section className="section pt-0">
        <div className="mx-auto max-w-7xl grid gap-8">
          {groups.map((g) => (
            <div
              key={g.id}
              className="card-soft p-8 md:p-10"
              data-testid={`menu-group-${g.id}`}
            >
              <div className="flex items-center gap-3">
                <span className={`h-12 w-12 grid place-items-center rounded-full ${g.accent}`}>
                  <g.icon className="h-5 w-5" />
                </span>
                <h2 className="font-serif text-2xl sm:text-3xl text-sakura-ink">{g.title}</h2>
              </div>
              {g.subtitle && (
                <p className="mt-2 text-sakura-soft italic" data-testid={`menu-subtitle-${g.id}`}>{g.subtitle}</p>
              )}
              <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {g.items.map((it) => (
                  <div
                    key={it.name}
                    className="rounded-2xl border border-sakura-200 bg-sakura-50 p-5 flex flex-col gap-2"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-serif text-lg text-sakura-ink leading-snug">{it.name}</h3>
                    </div>
                    <span className="text-sm font-semibold text-sakura-500 bg-sakura-200 rounded-full px-3 py-1 self-start whitespace-nowrap">
                      {it.price}
                    </span>
                    {it.desc && <p className="text-sm text-sakura-soft">{it.desc}</p>}
                  </div>
                ))}
              </div>
              {g.footnote && (
                <p className="mt-5 text-sm text-sakura-soft italic" data-testid={`menu-footnote-${g.id}`}>
                  {g.footnote}
                </p>
              )}
            </div>
          ))}

          {/* Party Packages */}
          <div className="card-soft p-8 md:p-12" data-testid="party-packages">
            <div className="text-center max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-3">
                <span className="h-12 w-12 grid place-items-center rounded-full bg-sakura-300/40 text-sakura-500">
                  <Gift className="h-5 w-5" />
                </span>
                <h2 className="font-serif text-3xl sm:text-4xl text-sakura-ink">
                  Party Packages <span className="text-sakura-gold">✨</span>
                </h2>
              </div>
              <p className="mt-4 text-sakura-soft">
                Perfect for birthdays, baby showers, graduations, weddings, church events, and dessert tables. Each package includes a beautiful mix of custom treats designed to match your theme, colors, and occasion.
              </p>
            </div>

            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {partyPackages.map((p) => (
                <div
                  key={p.id}
                  className={`relative rounded-3xl border p-7 flex flex-col gap-4 transition-shadow hover:shadow-sakura ${
                    p.featured
                      ? "border-sakura-gold/60 bg-gradient-to-br from-sakura-50 via-white to-sakura-100"
                      : "border-sakura-200 bg-sakura-50"
                  }`}
                  data-testid={`party-package-${p.id}`}
                >
                  {p.featured && (
                    <span className="absolute -top-3 right-6 bg-sakura-gold text-white text-[11px] font-semibold uppercase tracking-wide px-3 py-1 rounded-full shadow-sakura">
                      Most Luxurious
                    </span>
                  )}
                  <div>
                    <h3 className="font-serif text-2xl text-sakura-ink leading-snug">{p.title}</h3>
                    {p.subtitle && (
                      <p className="mt-1 text-sm font-semibold text-sakura-500 uppercase tracking-wide">{p.subtitle}</p>
                    )}
                    <p className="mt-1 text-sm text-sakura-soft italic">{p.serves}</p>
                  </div>
                  <span className="text-sm font-semibold text-sakura-500 bg-sakura-200 rounded-full px-3 py-1 self-start whitespace-nowrap">
                    {p.price}
                  </span>
                  <ul className="space-y-2">
                    {p.includes.map((line) => (
                      <li key={line} className="flex items-start gap-2 text-sm text-sakura-ink">
                        <CheckCircle2 className="h-4 w-4 text-sakura-500 mt-[3px] shrink-0" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <p
              className="mt-8 text-center text-sm text-sakura-soft italic max-w-2xl mx-auto"
              data-testid="party-package-treats-note"
            >
              Customers may choose a mix of treats including cake pops, dipped strawberries, dipped Oreos, dipped pretzel rods, crispy treats, and other custom treats.
            </p>

            <div className="mt-6 grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
              <div className="rounded-2xl border border-sakura-200 bg-sakura-50/70 p-5 text-sm text-sakura-ink leading-relaxed">
                <p>
                  <b className="text-sakura-500">Final pricing may vary</b> depending on treat selections, design details, theme complexity, custom colors, edible images, names, florals, metallic details, and packaging.
                </p>
              </div>
              <div className="rounded-2xl border border-sakura-gold/40 bg-gradient-to-br from-sakura-50 via-white to-sakura-100 p-5 text-sm text-sakura-ink leading-relaxed">
                <p>
                  A <b className="text-sakura-500">50% non-refundable deposit</b> is required to secure all party package orders, with the remaining balance due before pickup or delivery.
                </p>
              </div>
            </div>
          </div>

          {/* Payment & Deposit */}
          <div className="card-soft p-8 md:p-10 bg-sakura-100/60 border-sakura-300/50" data-testid="payment-policy">
            <div className="flex items-start gap-3">
              <span className="h-12 w-12 grid place-items-center rounded-full bg-sakura-200 text-sakura-500 shrink-0"><Wallet className="h-5 w-5" /></span>
              <div>
                <h2 className="font-serif text-2xl text-sakura-ink">Payment & Deposit</h2>
                <p className="mt-3 text-sakura-ink leading-relaxed">
                  A deposit is required to secure all custom orders. Final pricing may vary based on design details. Orders under $30 require full payment.
                </p>
              </div>
            </div>
          </div>

          {/* Policies & Order Information */}
          <div className="card-soft p-8 md:p-12" data-testid="policies-section">
            <div className="text-center max-w-2xl mx-auto">
              <span className="chip"><Sparkles className="h-3 w-3" /> Important Info</span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-sakura-ink">
                Policies & Order Information <span className="text-sakura-gold">✨</span>
              </h2>
              <p className="mt-3 text-sakura-soft">
                Please review before placing your order — these policies help me deliver the best experience for every customer.
              </p>
            </div>

            <div className="mt-10 grid md:grid-cols-2 gap-5">
              {policies.map((p) => (
                <div
                  key={p.id}
                  className="rounded-3xl border border-sakura-200 bg-sakura-50 p-6 md:p-7"
                  data-testid={`policy-${p.id}`}
                >
                  <h3 className="font-serif text-xl text-sakura-ink uppercase tracking-wide text-[15px] md:text-base">
                    {p.title}
                  </h3>
                  <ul className="mt-4 space-y-2.5">
                    {p.items.map((line) => (
                      <li key={line} className="flex items-start gap-2 text-sm text-sakura-ink leading-relaxed">
                        <CheckCircle2 className="h-4 w-4 text-sakura-500 mt-[3px] shrink-0" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing Note */}
          <div className="card-soft p-8 md:p-10 bg-sakura-100/60 border-sakura-300/50">
            <div className="flex items-start gap-3">
              <CandyCane className="h-5 w-5 text-sakura-500 mt-1 shrink-0" />
              <p className="text-sakura-ink">
                <b>A note on pricing:</b> Final pricing depends on design complexity, quantity, and theme details. A custom quote will be provided for all orders.
              </p>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/custom-orders" className="btn-primary" data-testid="menu-cta-order"><Sparkles className="h-4 w-4" /> Request a Quote</Link>
              <Link to="/faq" className="btn-secondary" data-testid="menu-cta-faq">Read FAQ</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
